import React, { useEffect, useMemo, useRef, useState } from "react";
import { autocompleteLocations } from "../../api/vehicleApi";
import { motion } from "framer-motion";

export default function LocationAutoInput({
  label,
  value,
  onChange,     // (string) => void
  onSelect,     // (string) => void  (optional; defaults to onChange)
  placeholder = "Start typing a location…",
  required = false,
  disabled = false,
  id,
  wrapperClassName = "",
  labelClassName = "",
  inputClassName = "",
  leading = null,
  trailing = null,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const sessionTokenRef = useRef("");
  const deb = useRef(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const selectHandler = useMemo(
    () => onSelect || onChange,
    [onSelect, onChange]
  );

  // click outside to close
  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!(open || loading)) return;
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 768) return;
    if (!inputRef.current) return;

    const adjustIntoView = () => {
      const rect = inputRef.current.getBoundingClientRect();
      const viewportHeight =
        window.visualViewport?.height || window.innerHeight || 0;
      const safeTop = 88;
      const desiredBottom = Math.max(safeTop + 220, viewportHeight - 260);

      if (rect.bottom > desiredBottom) {
        const delta = rect.bottom - desiredBottom;
        window.scrollBy({ top: delta, behavior: "smooth" });
      } else if (rect.top < safeTop) {
        window.scrollBy({ top: rect.top - safeTop, behavior: "smooth" });
      }
    };

    const timer = window.setTimeout(adjustIntoView, 80);
    window.visualViewport?.addEventListener("resize", adjustIntoView);

    return () => {
      window.clearTimeout(timer);
      window.visualViewport?.removeEventListener("resize", adjustIntoView);
    };
  }, [open, loading]);

  const handleInput = (val) => {
    onChange(val);
    if (deb.current) clearTimeout(deb.current);

    const q = val.trim();
    if (q.length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    deb.current = setTimeout(async () => {
      try {
        if (!sessionTokenRef.current) sessionTokenRef.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const list = await autocompleteLocations(q, { sessionToken: sessionTokenRef.current });
        setResults(list || []);
        setOpen((list || []).length > 0);
      } catch {
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  return (
    <div ref={boxRef} className={`relative ${wrapperClassName}`}>
      {label && (
        <label htmlFor={id} className={`text-sm font-medium ${labelClassName}`}>
          {label}{required ? " *" : ""}
        </label>
      )}
      <div className="relative">
        {leading ? (
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3">
            {leading}
          </div>
        ) : null}
        <input
          ref={inputRef}
          id={id}
          className={`mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 ${inputClassName}`}
          value={value}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => value?.trim()?.length >= 3 && results.length && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
        />
        {trailing ? (
          <div className="absolute inset-y-0 right-0 z-10 flex items-center pr-3">
            {trailing}
          </div>
        ) : null}
      </div>

      {(open || loading) && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute z-40 mt-2 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          style={{
            maxHeight:
              typeof window !== "undefined" && window.innerWidth < 768
                ? "min(42vh, 320px)"
                : undefined,
          }}
        >
          <div className="bg-slate-950 px-4 py-2 text-[11px] font-medium tracking-wide text-white/70 md:hidden">
            Search results
          </div>
          {loading && (
            <div className="px-4 py-3 text-sm text-slate-500">Searching…</div>
          )}
          {!loading && (
            <div className="max-h-[min(42vh,320px)] overflow-auto overscroll-contain md:max-h-56">
              {results.map((s, idx) => (
                <button
                  type="button"
                  key={s.placeId || `${s.fullText}-${idx}`}
                  className="w-full border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50 last:border-b-0"
                  onClick={() => {
                    selectHandler(s.fullText);
                    sessionTokenRef.current = "";
                    setResults([]);
                    setOpen(false);
                  }}
                >
                  <div className="text-sm font-medium text-slate-800">{s.primaryText || s.fullText}</div>
                  {s.secondaryText ? <div className="mt-0.5 text-xs text-slate-500">{s.secondaryText}</div> : null}
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
